import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { PolicyType } from '../types';
import { AiAlert, Alert, DaswatchAlert, DeviationAlert } from '../types/Alert';
import { Dasair } from '../types/Device';
import { WebSocketAlert, WebSocketAlertReceived } from '../types/Websocket';

import { getAreasOfProject } from '../apis/AreaApi';
import { getProjectDasairs } from '../apis/DasAirApi';
import { getProjectWorkers } from '../apis/DasloopApi';
import { getEquipmentById } from '../apis/DaspowerApi';
import { getProjectPlants } from '../apis/DastrackApi';
import { getProjectVehicles } from '../apis/DastrackVApi';
import { getProjectOfResources } from '../apis/ResourceApi';
import { useAppSelector } from '../hooks';

import {
  AlertType,
  DaswatchAlertType,
  getDasConcreteAlertType,
} from '../utils/getAlertIcon';
import { getDasairAlertType } from '../utils/getDasairType';
import { findTheClosestPipe } from '../utils/getWaterLevel';
import toAlertDescription from '../utils/toAlertDescription';

const useAlertSnackBar = (projectId: string, websocket: WebSocketAlert) => {
  const projectRole = useAppSelector((store) => store.projects.role);
  const policies = useAppSelector((store) => store.projects.policies);
  const policyMap = new Map<PolicyType, boolean>();
  policies.forEach((policy) => {
    policyMap.set(policy.name, true);
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const workerReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOneWorkerGroup');

  const plantReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOnePlantGroup');

  const equipmentReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOneEquipmentGroup');

  const vehicleReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOneVehicleGroup');

  const areaReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadArea');

  const dasairReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadDasair');

  const dasConcreteReadable =
    projectRole === 'owner' || projectRole === 'admin';

  const aiReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOneAiGroup');

  useEffect(() => {
    const showSnackbar = async (ws: WebSocketAlertReceived) => {
      let resourceName: string | undefined;
      let areaName: string | undefined;
      let areaType: string | undefined;
      let dasair: Dasair | undefined;
      switch (ws.type) {
        case 'dasloop':
        case 'dasgas':
          if (workerReadable && 'workerId' in ws.data) {
            resourceName = await getProjectWorkers({
              projectId,
              params: {
                id: ws.data.workerId ?? undefined,
              },
            }).then((res) => res.data.data?.[0]?.name);
          }
          break;
        case 'daswatch':
          resourceName = ws.data.dasId;
          break;
        case 'dastrack':
          if (plantReadable && 'plantId' in ws.data) {
            resourceName = await getProjectPlants({
              projectId,
              params: {
                id: ws.data.plantId ?? undefined,
              },
            }).then((res) => res.data.data?.[0]?.name);
          }
          break;
        case 'dascas_g':
          if (plantReadable) {
            resourceName = await getProjectPlants({
              projectId,
              params: {
                id: (ws.data as Alert<'dascas'>).plantId ?? undefined,
              },
            }).then((res) => res.data.data?.[0]?.name);
          }
          break;
        case 'dastemp':
        case 'daspower':
        case 'daslock':
          if (equipmentReadable) {
            resourceName = await getEquipmentById(
              projectId,
              (ws.data as Alert<'dastemp' | 'daspower' | 'daslock'>).equipment
                ?.id ?? '',
            ).then((res) => res.data.data.name);
          }
          break;
        case 'deviation':
          if (vehicleReadable && 'vehicleId' in ws.data) {
            resourceName = await getProjectVehicles({
              projectId,
              params: {
                id: ws.data.vehicleId ?? undefined,
              },
            }).then((res) => res.data.data?.[0]?.name);
          }
          if (areaReadable && 'areaId' in ws.data) {
            const res = await getAreasOfProject(projectId, {
              id: ws.data.areaId ?? undefined,
              selectVisible: false,
            });
            areaName = res.data.data?.[0]?.name;
            areaType = res.data.data?.[0]?.type;
          }
          break;
        case 'dasair':
          if (dasairReadable && 'endpointId' in ws.data) {
            dasair = await getProjectDasairs({
              projectId,
              params: {
                id: ws.data.endpointId ?? undefined,
              },
            }).then((res) => res.data.data?.[0]);
          }
          break;
        case 'area':
          if (workerReadable && 'workerId' in ws.data) {
            resourceName = await getProjectWorkers({
              projectId,
              params: {
                id: ws.data.workerId ?? undefined,
              },
            }).then((res) => res.data.data?.[0]?.name);
          }
          if (areaReadable && 'areaId' in ws.data) {
            const res = await getAreasOfProject(projectId, {
              id: ws.data.areaId ?? undefined,
              selectVisible: false,
            });
            areaName = res.data.data?.[0]?.name;
            areaType = res.data.data?.[0]?.type;
          }
          break;
        case 'dasconcrete':
          if (dasConcreteReadable && 'resourceId' in ws.data) {
            resourceName = await getProjectOfResources({
              projectId,
              resourceType: 'field',
              params: {
                id: ws.data.resourceId ?? undefined,
              },
            }).then((res) => res.data.data?.[0]?.name);
          }
          break;
        case 'ai':
          if (aiReadable && 'resourceId' in ws.data) {
            resourceName = await getProjectOfResources({
              projectId,
              resourceType: 'ai',
              params: {
                id: ws.data.resourceId ?? undefined,
              },
            }).then((res) => res.data.data?.[0]?.name);
          }
          break;
      }

      let title: string = '';
      let alertType: AlertType | DaswatchAlertType = AlertType.AREA_ALERT;
      let timestamp: string = new Date().toISOString();
      const type = 'notify';

      if (ws.type === 'deviation') {
        timestamp = (ws.data as Alert<'deviation'>).exitedAt;
      } else {
        timestamp = (
          ws.data as Exclude<WebSocketAlertReceived['data'], DeviationAlert>
        ).timestamp;
      }

      const { description, descriptionNode } = toAlertDescription(
        ws.type,
        ws.data,
        t,
        type,
        {
          areaName:
            ws.type === 'area'
              ? (areaName ?? `${t('no-permission')} ${t('or')} ${t('deleted')}`)
              : undefined,
          areaType,
          pipes: ws.pipes,
        },
      );

      if (ws.type === 'dasloop') {
        const dasAlert = ws.data as Alert<'dasloop'>;
        const workerName = resourceName;

        if (dasAlert.alert.type === AlertType.CAP_OFF) {
          title = workerName
            ? `${workerName} (${dasAlert.dasId}) ${t(
                `alert-type.${dasAlert.alert.type}`,
              )}`
            : dasAlert.workerId
              ? `${t('no-permission')} (${dasAlert.dasId})`
              : `${t('no-assigned-worker')} (${dasAlert.dasId})`;
        } else {
          title = workerName
            ? `${workerName} (${dasAlert.dasId})`
            : dasAlert.workerId
              ? `${t('no-permission')} (${dasAlert.dasId})`
              : `${t('no-assigned-worker')} (${dasAlert.dasId})`;
        }

        alertType = dasAlert.alert.type;
      } else if (ws.type === 'dastrack') {
        const dasAlert = ws.data as Alert<'dastrack'>;
        const plantName = resourceName;

        title = plantName
          ? `${plantName} (${dasAlert.dasId})`
          : dasAlert.plantId
            ? `${t('no-permission')} (${dasAlert.dasId})`
            : `${t('no-assigned-plant')} (${dasAlert.dasId})`;
        alertType = dasAlert.alertType;
      } else if (ws.type === 'daspower') {
        const dasAlert = ws.data as Alert<'daspower'>;
        const equipmentName = resourceName;
        title = equipmentName
          ? `${equipmentName} (${dasAlert.dasId})`
          : dasAlert.equipment?.id
            ? `${t('no-permission')} (${dasAlert.dasId})`
            : `${t('no-assigned-equipment')} (${dasAlert.dasId})`;
        alertType = AlertType.DASPOWER_ALERT;
      } else if (ws.type === 'area') {
        const dasAlert = ws.data as Alert<'area'>;
        const workerName = resourceName;
        title = workerName
          ? `${workerName} (${dasAlert.dasId})`
          : dasAlert.areaId
            ? `${t('no-permission')} (${dasAlert.dasId})`
            : `${t('deleted')} (${dasAlert.dasId})`;
        alertType = AlertType.AREA_ALERT;
      } else if (ws.type === 'dastemp') {
        const dasAlert = ws.data as Alert<'dastemp'>;
        const equipmentName = resourceName;
        title = equipmentName
          ? `${equipmentName} (${dasAlert.dasId})`
          : dasAlert.equipment?.id
            ? `${t('no-permission')} (${dasAlert.dasId})`
            : `${t('no-assigned-equipment')} (${dasAlert.dasId})`;
        alertType = AlertType.AREA_ALERT;
      } else if (ws.type === 'dasair') {
        const dasAlert = ws.data as Alert<'dasair'>;
        title =
          dasair?.dasId ??
          (dasAlert.endpointId
            ? `${t('no-permission')} (${dasair?.dasId})`
            : `${t('deleted')} (${dasair?.dasId})`);
        alertType = getDasairAlertType(dasAlert);
      } else if (ws.type === 'deviation') {
        const deviationAlert = ws.data as Alert<'deviation'>;
        title = `${resourceName} ${t('leave')} ${
          areaName
            ? `${areaName} (${deviationAlert.dasId})`
            : `${t('no-permission')} (${deviationAlert.dasId})`
        }`;
        alertType = AlertType.DASTRACK_V_LEAVE_ALERT;
      } else if (ws.type === 'daswater') {
        const dasAlert = ws.data as Alert<'daswater'>;
        const pipeName = findTheClosestPipe(ws.pipes, dasAlert)?.name;
        title = pipeName
          ? `${pipeName} (${dasAlert.dasId})`
          : dasAlert.resourceId
            ? `${t('no-permission')} (${dasAlert.dasId})`
            : `${t('no-assigned-pipe')} (${dasAlert.dasId})`;

        if (dasAlert.field === 'battery') {
          alertType = AlertType.DASWATER_L_LOWBATTERYALARM;
        } else if (dasAlert.field === 'distance') {
          alertType = AlertType.DASWATER_L_DISTANCE;
        } else if (dasAlert.field === 'isTilt') {
          alertType = AlertType.DASWATER_L_TILT;
        } else {
          alertType = AlertType.DASWATER_L;
        }
      } else if (ws.type === 'dasgas') {
        const dasAlert = ws.data as Alert<'dasgas'>;
        const workerName = resourceName;
        title = workerName
          ? `${workerName} (${dasAlert.dasId})`
          : dasAlert.workerId
            ? `${t('no-permission')} (${dasAlert.dasId})`
            : `${t('no-assigned-worker')} (${dasAlert.dasId})`;
        alertType = dasAlert.alert.type;
      } else if (ws.type === 'daslock') {
        const dasAlert = ws.data as Alert<'daslock'>;
        const equipmentName = resourceName;
        title = equipmentName
          ? `${equipmentName} (${dasAlert.dasId})`
          : dasAlert.equipment?.id
            ? `${t('no-permission')} (${dasAlert.dasId})`
            : `${t('no-assigned-equipment')} (${dasAlert.dasId})`;
        alertType = dasAlert.alert.type;
      } else if (ws.type === 'dascas_g') {
        const dasAlert = ws.data as Alert<'dascas'>;
        const plantName = resourceName;
        title = plantName
          ? `${plantName} (${dasAlert.dasId})`
          : dasAlert.plantId
            ? `${t('no-permission')} (${dasAlert.dasId})`
            : `${t('no-assigned-plant')} (${dasAlert.dasId})`;
        alertType = dasAlert.alertType;
      } else if (ws.type === 'dasconcrete') {
        const dasAlert = ws.data as Alert<'dasconcrete'>;
        const structureName = resourceName;
        title = structureName
          ? `${structureName} (${dasAlert.dasId})`
          : dasAlert.resourceId
            ? `${t('no-permission')} (${dasAlert.dasId})`
            : `${t('no-assigned-structure')} (${dasAlert.dasId})`;
        alertType = getDasConcreteAlertType(dasAlert);
      } else if (ws.type === 'ai') {
        const dasAlert = ws.data as AiAlert;
        const structureName = resourceName;
        title = structureName
          ? `${structureName} (${dasAlert.dasId})`
          : `${t('no-permission')} ${t('or')} ${t(
              'no-assigned-structure',
            )} (${dasAlert.dasId})`;
        alertType = dasAlert.field;
      } else if (ws.type === 'daswatch') {
        const dasAlert = ws.data as DaswatchAlert;
        title = `${ws.data.dasId} ${dasAlert.field}`;
        alertType = dasAlert.field as DaswatchAlertType;
      }

      enqueueSnackbar('', {
        variant: 'alertNotification',
        key: `alert-notification-${ws.data.id}`,
        data: {
          alertType,
          title,
          description,
          descriptionNode,
          timestamp,
        },
      });
    };
    if (websocket) {
      showSnackbar(websocket);
    }
  }, [projectId, websocket, t]);
};

export default useAlertSnackBar;
